// AUTO-GENERATED from servers/common-servers-v2/helpers/FormHelper/regex.js by servers/common-servers-v2/scripts/sync-form-helper.js.
// Do not edit: change the source and run `node scripts/sync-form-helper.js`.
/* eslint-disable */
import { LIMITS } from "./constants.js";

/*
 * Admin-written regexes (text / textarea `data.regex`) are matched against
 * visitor input, so a catastrophic-backtracking pattern is a DoS. A regex is
 * accepted at save time only when it compiles, is short and passes this
 * conservative static check. It rejects, rather than proves safe:
 *   - backreferences;
 *   - a repeat (* + {n,} {n,m}) of a group that itself contains a repeat: (a+)+ (a*)* (\d+\s?)+ ;
 *   - a repeat (more than three times) of a group that holds an optional part: (a?){30}a{30} - every copy can match or skip, so
 *     the work doubles with each ;
 *   - a repeat of a group of alternatives that can start with the same character: (a|aa)+ (\w|\d)* ;
 *   - more than MAX_VARIABLE_REPEATS variable repeats in total (polynomial blow-up).
 * It can refuse a few harmless patterns; the admin rewrites them.
 */

const MAX_VARIABLE_REPEATS = 5;
const WILDCARD = "*";

// Reads a quantifier at `index`. Returns { min, max, end } or null when there is none.
const read_quantifier = (source, index) => {
    const char = source[index];
    let min;
    let max;
    let end = index + 1;
    if (char === "*") {
        min = 0;
        max = Infinity;
    } else if (char === "+") {
        min = 1;
        max = Infinity;
    } else if (char === "?") {
        min = 0;
        max = 1;
    } else if (char === "{") {
        const match = /^\{(\d+)(?:(,)(\d*))?\}/.exec(source.slice(index));
        if (!match) {
            return null;
        }
        min = Number(match[1]);
        max = match[2] ? (match[3] === "" ? Infinity : Number(match[3])) : min;
        end = index + match[0].length;
    } else {
        return null;
    }
    if (source[end] === "?") {
        end += 1;
    }
    return { min, max, end };
};

// More than one way to match: can the repeat try different lengths on the same input?
const is_variable = (quantifier) => quantifier.max > 1 && quantifier.min !== quantifier.max;

const check_regex = (source) => {
    if (typeof source !== "string" || source.length === 0) {
        return { ok: false, reason: "empty" };
    }
    if (source.length > LIMITS.REGEX_MAX_LENGTH) {
        return { ok: false, reason: "too_long" };
    }
    try {
        new RegExp(source);
    } catch {
        return { ok: false, reason: "invalid" };
    }

    const new_frame = () => ({ has_variable_repeat: false, has_optional: false, first_atoms: [], branch_open: true, has_alternation: false });
    const stack = [new_frame()];
    let variable_repeats = 0;
    let i = 0;

    const add_atom = (atom) => {
        const frame = stack[stack.length - 1];
        if (frame.branch_open) {
            frame.first_atoms.push(atom);
            frame.branch_open = false;
        }
    };

    // Applies a quantifier that follows an atom or a closed group. Returns false when unsafe.
    const apply_quantifier = (quantifier, target_frame) => {
        if (quantifier && quantifier.min === 0 && quantifier.max === 1) {
            stack[stack.length - 1].has_optional = true;
        }
        if (!quantifier || quantifier.max <= 1) {
            return true;
        }
        const variable = is_variable(quantifier);
        if (variable) {
            variable_repeats += 1;
        }
        if (target_frame) {
            // any repeat (even a fixed {20}) of a group that already holds a variable repeat multiplies the work
            if (target_frame.has_variable_repeat) {
                return false;
            }
            // an optional part repeated many times: each copy may match or not, so the ways to match grow as 2^n
            if (target_frame.has_optional && quantifier.max > 3) {
                return false;
            }
            if (variable && target_frame.has_alternation) {
                const atoms = target_frame.first_atoms;
                const overlap = atoms.includes(WILDCARD) || new Set(atoms).size !== atoms.length;
                if (overlap) {
                    return false;
                }
            }
        }
        if (variable) {
            stack[stack.length - 1].has_variable_repeat = true;
        }
        return true;
    };

    while (i < source.length) {
        const char = source[i];
        if (char === "\\") {
            const next = source[i + 1] ?? "";
            if (/[1-9]/.test(next) || (next === "k" && source[i + 2] === "<")) {
                return { ok: false, reason: "backreference" };
            }
            add_atom(/[dDwWsSbB]/.test(next) ? WILDCARD : next);
            i += 2;
        } else if (char === "[") {
            let j = i + 1;
            if (source[j] === "^") {
                j += 1;
            }
            if (source[j] === "]") {
                j += 1;
            }
            while (j < source.length && source[j] !== "]") {
                j += source[j] === "\\" ? 2 : 1;
            }
            add_atom(WILDCARD);
            i = j + 1;
        } else if (char === "(") {
            const frame = new_frame();
            stack.push(frame);
            i += 1;
            if (source[i] === "?") {
                if (source[i + 1] === "<" && source[i + 2] !== "=" && source[i + 2] !== "!") {
                    i = source.indexOf(">", i) + 1;
                } else {
                    i += source[i + 1] === "<" ? 3 : 2;
                }
            }
        } else if (char === ")") {
            const frame = stack.pop();
            const parent = stack[stack.length - 1];
            if (frame.has_variable_repeat) {
                parent.has_variable_repeat = true;
            }
            if (frame.has_optional) {
                parent.has_optional = true;
            }
            add_atom(WILDCARD);
            i += 1;
            const quantifier = read_quantifier(source, i);
            if (quantifier) {
                if (!apply_quantifier(quantifier, frame)) {
                    return { ok: false, reason: frame.has_variable_repeat || frame.has_optional ? "nested_quantifier" : "ambiguous_alternation" };
                }
                i = quantifier.end;
            }
        } else if (char === "|") {
            const frame = stack[stack.length - 1];
            frame.has_alternation = true;
            frame.branch_open = true;
            i += 1;
        } else if (char === ".") {
            add_atom(WILDCARD);
            i += 1;
            const quantifier = read_quantifier(source, i);
            if (quantifier) {
                apply_quantifier(quantifier, null);
                i = quantifier.end;
            }
        } else if (char === "^" || char === "$") {
            i += 1;
        } else {
            add_atom(char);
            i += 1;
            const quantifier = read_quantifier(source, i);
            if (quantifier) {
                apply_quantifier(quantifier, null);
                i = quantifier.end;
            }
        }
        // A quantifier right after an escape or a class applies to that atom.
        if (i > 0 && (source[i - 1] === "]" || /\\./.test(source.slice(Math.max(0, i - 2), i)))) {
            const quantifier = read_quantifier(source, i);
            if (quantifier) {
                apply_quantifier(quantifier, null);
                i = quantifier.end;
            }
        }
    }

    if (variable_repeats > MAX_VARIABLE_REPEATS) {
        return { ok: false, reason: "too_many_repeats" };
    }
    return { ok: true };
};

// Compiled regex, or null when the pattern is not acceptable.
const compile_regex = (source) => {
    if (!check_regex(source).ok) {
        return null;
    }
    try {
        return new RegExp(source);
    } catch {
        return null;
    }
};

export {
    check_regex,
    compile_regex,
};
